import {
  HE_DRUG_ADVICE_DOSAGE,
  HE_DRUG_ADVICE_DURATION,
  HE_DRUG_ADVICE_FREQUENCY,
  HE_DRUG_ADVICE_NOTE,
  HE_DRUG_ADVICE_TIMING,
  HE_DRUG_ADVICE_TYPE,
  HE_KEY_PSYCH_ASSESSMENTS,
  IDB_STORES,
} from 'src/consumer/constants'
import { createERMDatabase } from './indexedDb'
import {
  decryptIdbRecord,
  decryptIdbRecords,
  encryptIdbRecord,
  isIdbEncryptionSupported,
} from './idbCrypto'

// All records are encrypted at rest (AES-GCM via idbCrypto) — only the primary key
// stays readable inside IndexedDB. Because the native indexes therefore cannot see
// record fields anymore, every search runs over decrypted records held in an
// in-memory cache (kept up to date by the write helpers), which is also what makes
// per-keystroke searches cheap.

async function getDB() {
  const db = await createERMDatabase()
  return db
}

// indexName (as used by the old index-based search API) -> record field
const INDEX_FIELD_BY_NAME = {}
Object.values(IDB_STORES).forEach((storeConfig) => {
  storeConfig.indexes.forEach(({ name, field }) => {
    INDEX_FIELD_BY_NAME[name] = field
  })
})

// decrypted-records cache: storeName -> Map(id -> record)
const decryptedStoreCache = new Map()

// must be called whenever the underlying database is wiped (e.g. force resync)
export function clearDecryptedStoreCache() {
  decryptedStoreCache.clear()
  compactTitleCache.clear()
  sortedTitleRecordCache.clear()
}

/**
 * Raw record count for one store — used by the remote-debug IndexedDB report. Uses the
 * native count() so the 300k+-record drugs store is never read or decrypted for a count.
 */
export async function countRecordsInStore(storeName) {
  const db = await getDB()
  return await db.count(storeName)
}

/** Empties one store (per-table reset in settings) and drops its caches. */
export async function clearStoreInIdb(storeName) {
  const db = await getDB()
  await db.clear(storeName)
  decryptedStoreCache.delete(storeName)
  invalidateCompactTitleCache(storeName)
}

// ------------------------------------------------------------- fast title search
// Works on the plaintext catalog stores: prefix queries are a titleIDX range read
// (milliseconds, constant in store size); contains-matching falls back to a compact
// in-memory (titleLower, id) list — never full records, never decryption.

const TITLE_PREFIX_LIMIT = 100
const TITLE_CONTAINS_LIMIT = 60
const TITLE_RESULT_LIMIT = 50

async function searchStoreByTitlePrefix(storeName, prefixLower, limit) {
  const db = await getDB()
  const range = window.IDBKeyRange.bound(prefixLower, prefixLower + '￿')
  const storedRecords = await db.getAllFromIndex(storeName, 'titleIDX', range, limit)
  // plaintext records pass through unchanged; tolerates stray encrypted leftovers
  return await decryptIdbRecords(storedRecords)
}

const compactTitleCacheBuilds = new Map()

/**
 * Builds the compact (titleLower, id) list in PAGED batches with an event-loop yield
 * between batches — one giant getAll() over a 300k-record store materializes every
 * object in a single main-thread burst and visibly freezes slow machines. Batches keep
 * each burst to a few thousand small objects (~10ms), and only the two strings per
 * record are retained. Concurrent callers share one in-flight build.
 */
async function ensureCompactTitleCache(storeName) {
  if (compactTitleCache.has(storeName)) {
    return compactTitleCache.get(storeName)
  }
  if (compactTitleCacheBuilds.has(storeName)) {
    return await compactTitleCacheBuilds.get(storeName)
  }
  const build = (async () => {
    const db = await getDB()
    const pairs = []
    const BATCH = 4000
    let lowerBound = null
    while (true) {
      const range = lowerBound ? window.IDBKeyRange.lowerBound(lowerBound, true) : null
      const batch = await db.getAll(storeName, range, BATCH)
      if (batch.length === 0) {
        break
      }
      for (const record of batch) {
        if (record?.id && record?.lowerCaseIndexField) {
          pairs.push([record.lowerCaseIndexField, record.id])
        }
      }
      lowerBound = batch[batch.length - 1]?.id
      if (!lowerBound || batch.length < BATCH) {
        break
      }
      // let input events, paints and the dropdown itself run between batches
      await new Promise((resolve) => setTimeout(resolve, 0))
    }
    compactTitleCache.set(storeName, pairs)
    return pairs
  })()
  compactTitleCacheBuilds.set(storeName, build)
  try {
    return await build
  } finally {
    compactTitleCacheBuilds.delete(storeName)
  }
}

/**
 * Warms the search caches off the interaction path (called after sync completes and
 * from idle time) so the first real keystroke never pays the build cost.
 */
export function prewarmEmrSearchCaches() {
  const kickoff = () => {
    ensureCompactTitleCache(IDB_STORES.DRUGS.name).catch(() => {})
    ensureCompactTitleCache(IDB_STORES.GENERIC_DRUGS.name).catch(() => {})
  }
  try {
    if (typeof window !== 'undefined' && window.requestIdleCallback) {
      window.requestIdleCallback(kickoff, { timeout: 10000 })
    } else {
      setTimeout(kickoff, 3000)
    }
  } catch (error) {
    // prewarming is opportunistic
  }
}

/**
 * Index-backed title search over a plaintext catalog store: prefix hits first (capped
 * range read), then capped contains-matches for queries of 3+ chars. Returns raw
 * records, unranked — callers apply their own ordering.
 */
export async function searchStoreByTitleFast(storeName, searchTerm, { limit = TITLE_RESULT_LIMIT } = {}) {
  const query = String(searchTerm || '')
    .trim()
    .toLowerCase()
  if (!query) {
    return []
  }
  const prefixMatches = await searchStoreByTitlePrefix(storeName, query, TITLE_PREFIX_LIMIT)
  if (prefixMatches.length >= limit || query.length < 3) {
    return prefixMatches
  }
  const pairs = await ensureCompactTitleCache(storeName)
  const seen = new Set(prefixMatches.map((record) => record.id))
  const containsIds = []
  for (const [titleLower, id] of pairs) {
    if (!seen.has(id) && !titleLower.startsWith(query) && titleLower.includes(query)) {
      containsIds.push(id)
      if (containsIds.length >= TITLE_CONTAINS_LIMIT) {
        break
      }
    }
  }
  const containsRecords = await Promise.all(
    containsIds.map((id) => getRecordByIdFromStore(storeName, id)),
  )
  return [...prefixMatches, ...containsRecords.filter(Boolean)]
}

async function getDecryptedStoreMap(storeName) {
  if (decryptedStoreCache.has(storeName)) {
    return decryptedStoreCache.get(storeName)
  }
  const db = await getDB()
  const storedRecords = await db.getAll(storeName)
  const records = await decryptIdbRecords(storedRecords)
  const recordsById = new Map()
  records.forEach((record) => {
    if (record?.id) {
      recordsById.set(record.id, record)
    }
  })
  decryptedStoreCache.set(storeName, recordsById)

  // lazy migration: plaintext leftovers (written by an older app version or in a
  // non-secure dev context) are re-written encrypted in the background — readers
  // already have the data via the cache, so this never blocks anything
  const plaintextLeftovers = storedRecords.filter((record) => record && !record.__enc && record.id)
  if (plaintextLeftovers.length > 0 && isIdbEncryptionSupported()) {
    putRecordsInStore(storeName, plaintextLeftovers).catch((error) =>
      console.warn(
        `Could not re-encrypt ${plaintextLeftovers.length} legacy plaintext records in ${storeName}`,
        error,
      ),
    )
  }
  return recordsById
}

async function getAllRecordsFromStore(storeName) {
  const recordsById = await getDecryptedStoreMap(storeName)
  return Array.from(recordsById.values())
}

function compareHealthEntityTitles(a, b) {
  return String(a?.lowerCaseIndexField || a?.title || '').localeCompare(
    String(b?.lowerCaseIndexField || b?.title || ''),
  )
}

// Blank diagnosis pickers are opened frequently. IndexedDB returns encrypted legacy
// diagnosis records in primary-key order, not titleIDX order, so sort once per store
// revision and reuse the result until sync writes or tombstones invalidate it.
async function getAllRecordsSortedByTitle(storeName) {
  if (sortedTitleRecordCache.has(storeName)) {
    return sortedTitleRecordCache.get(storeName)
  }
  const sortedRecords = [...(await getAllRecordsFromStore(storeName))].sort(compareHealthEntityTitles)
  sortedTitleRecordCache.set(storeName, sortedRecords)
  return sortedRecords
}

// Public catalog stores are written PLAINTEXT so their IndexedDB indexes (titleIDX)
// stay usable — encrypting public data only defeated indexed search and forced the
// decrypt-everything-into-RAM pattern. Patient/master-list stores stay encrypted.
const PLAINTEXT_STORE_NAMES = new Set(
  Object.values(IDB_STORES)
    .filter((storeConfig) => storeConfig.plaintext)
    .map((storeConfig) => storeConfig.name),
)

// compact (titleLower, id) pairs per store for capped contains-search without
// materializing (or decrypting) full records; ~50 bytes/record instead of ~1KB
const compactTitleCache = new Map()
const sortedTitleRecordCache = new Map()

function invalidateCompactTitleCache(storeName) {
  compactTitleCache.delete(storeName)
  sortedTitleRecordCache.delete(storeName)
}

async function putRecordsInStore(storeName, records) {
  const validRecords = (records || []).filter((record) => record?.id)
  if (validRecords.length === 0) {
    return
  }
  const storedRecords = PLAINTEXT_STORE_NAMES.has(storeName)
    ? validRecords
    : await Promise.all(validRecords.map((record) => encryptIdbRecord(record, 'id')))

  const db = await getDB()
  const tx = db.transaction(storeName, 'readwrite')
  const store = tx.objectStore(storeName)
  for (const storedRecord of storedRecords) {
    await store.put(storedRecord)
  }
  await tx.done

  if (decryptedStoreCache.has(storeName)) {
    const recordsById = decryptedStoreCache.get(storeName)
    validRecords.forEach((record) => recordsById.set(record.id, record))
  }
  invalidateCompactTitleCache(storeName)
}

// Deletes records by id from an IndexedDB store — used to apply sync-delta tombstones (rows the
// server reports disabled/deleted since the client's cursor).
export async function deleteRecordsFromStore(storeName, ids) {
  const validIds = (ids || []).filter(Boolean)
  if (validIds.length === 0) {
    return
  }
  const db = await getDB()
  const tx = db.transaction(storeName, 'readwrite')
  const store = tx.objectStore(storeName)
  for (const id of validIds) {
    await store.delete(id)
  }
  await tx.done

  if (decryptedStoreCache.has(storeName)) {
    const recordsById = decryptedStoreCache.get(storeName)
    validIds.forEach((id) => recordsById.delete(id))
  }
  invalidateCompactTitleCache(storeName)
}

async function getRecordByIdFromStore(storeName, id) {
  if (!id) {
    return null
  }
  if (decryptedStoreCache.has(storeName)) {
    return decryptedStoreCache.get(storeName).get(id) || null
  }
  const db = await getDB()
  const storedRecord = await db.get(storeName, id)
  const record = await decryptIdbRecord(storedRecord)
  return record || null
}

function withTitleIndexField(item) {
  return { ...item, lowerCaseIndexField: item?.title?.toLowerCase() }
}

export function getFunctionForAddOrUpdateHealthEntitySingle(healthEntity, data) {
  return getFunctionForAddOrUpdateHealthEntityMulitple(healthEntity, [data])
}

export function getFunctionForAddOrUpdateHealthEntityMulitple(healthEntity, data) {
  switch (healthEntity) {
    case 'complaints':
      return addOrUpdateHealthComplaintsInIdb(data)
    case 'diagnosis':
      return addOrUpdateDiagnosisInIdb(data)
    case 'observations':
      return addOrUpdateObservationsInIdb(data)
    case 'advices':
      return addOrUpdateAdvicesInIdb(data)
    case 'instructions':
      return addOrUpdateInstructionsInIdb(data)
    case 'investigation':
      return addOrUpdateInvestigationInIdb(data)
    case HE_KEY_PSYCH_ASSESSMENTS:
      return addOrUpdatePsychAssessmentsInIdb(data)
    case HE_DRUG_ADVICE_DOSAGE:
    case HE_DRUG_ADVICE_DURATION:
    case HE_DRUG_ADVICE_FREQUENCY:
    case HE_DRUG_ADVICE_TIMING:
    case HE_DRUG_ADVICE_TYPE:
    case HE_DRUG_ADVICE_NOTE:
      return addOrUpdateDrugAdviceEntitiesInIdb(healthEntity, data)
    default:
      return null
  }
}

// IDB_STORES.HEALTH_COMPLAINTS.name:

export async function addOrUpdateHealthComplaintsInIdb(complaints) {
  await putRecordsInStore(
    IDB_STORES.HEALTH_COMPLAINTS.name,
    (complaints || []).filter((item) => item?.title).map(withTitleIndexField),
  )
}

export async function getAllHealthComplaintsFromIdb() {
  return await getAllRecordsFromStore(IDB_STORES.HEALTH_COMPLAINTS.name)
}

export async function searchHealthComplaintsInIdb(searchTerm) {
  if (!searchTerm) {
    return await getAllHealthComplaintsFromIdb()
  }
  return await searchItemsInIdb(searchTerm, IDB_STORES.HEALTH_COMPLAINTS.name, 'titleIDX', true)
}

export async function getHealthComplaintsByIds(healthComplaintIds) {
  return await getItemsByIds(healthComplaintIds, IDB_STORES.HEALTH_COMPLAINTS.name)
}

// IDB_STORES.DIAGNOSIS.name:

export async function addOrUpdateDiagnosisInIdb(diagnosisList) {
  await putRecordsInStore(
    IDB_STORES.DIAGNOSIS.name,
    (diagnosisList || []).filter((item) => item?.title).map(withTitleIndexField),
  )
}

export async function getAllDiagnosisFromIdb() {
  return await getAllRecordsSortedByTitle(IDB_STORES.DIAGNOSIS.name)
}

export async function searchDiagnosisInIdb(searchTerm) {
  if (!searchTerm) {
    return await getAllDiagnosisFromIdb()
  }
  return await searchItemsInIdb(searchTerm, IDB_STORES.DIAGNOSIS.name, 'titleIDX', true)
}

export async function getDiagnosisByIds(diagnosisIds) {
  return await getItemsByIds(diagnosisIds, IDB_STORES.DIAGNOSIS.name)
}

// IDB_STORES.OBSERVATIONS.name:

export async function addOrUpdateObservationsInIdb(observationsList) {
  await putRecordsInStore(
    IDB_STORES.OBSERVATIONS.name,
    (observationsList || []).filter((item) => item?.title).map(withTitleIndexField),
  )
}

export async function getAllObservationsInIdb() {
  return await getAllRecordsFromStore(IDB_STORES.OBSERVATIONS.name)
}

export async function searchObservationsInIdb(searchTerm) {
  if (!searchTerm) {
    return await getAllObservationsInIdb()
  }
  return await searchItemsInIdb(searchTerm, IDB_STORES.OBSERVATIONS.name, 'titleIDX', true)
}

export async function getObservationsByIds(observationsIds) {
  return await getItemsByIds(observationsIds, IDB_STORES.OBSERVATIONS.name)
}

// IDB_STORES.ADVICE.name:

export async function addOrUpdateAdvicesInIdb(advicesList) {
  await putRecordsInStore(
    IDB_STORES.ADVICE.name,
    (advicesList || []).filter((item) => item?.title).map(withTitleIndexField),
  )
}

export async function getAllAdvicesInIdb() {
  return await getAllRecordsFromStore(IDB_STORES.ADVICE.name)
}

export async function searchAdvicesInIdb(searchTerm) {
  if (!searchTerm) {
    return await getAllAdvicesInIdb()
  }
  return await searchItemsInIdb(searchTerm, IDB_STORES.ADVICE.name, 'titleIDX', true)
}

export async function getAdvicesByIds(advicesByIds) {
  return await getItemsByIds(advicesByIds, IDB_STORES.ADVICE.name)
}

// IDB_STORES.INSTRUCTIONS.name:

export async function addOrUpdateInstructionsInIdb(instructionsList) {
  await putRecordsInStore(
    IDB_STORES.INSTRUCTIONS.name,
    (instructionsList || []).filter((item) => item?.title).map(withTitleIndexField),
  )
}

export async function getAllInstructionsInIdb() {
  return await getAllRecordsFromStore(IDB_STORES.INSTRUCTIONS.name)
}

export async function searchInstructionsInIdb(searchTerm) {
  if (!searchTerm) {
    return await getAllInstructionsInIdb()
  }
  return await searchItemsInIdb(searchTerm, IDB_STORES.INSTRUCTIONS.name, 'titleIDX', true)
}

export async function getInstructionsByIds(instructionsByIds) {
  return await getItemsByIds(instructionsByIds, IDB_STORES.INSTRUCTIONS.name)
}

// IDB_STORES.INVESTIGATION.name:

export async function addOrUpdateInvestigationInIdb(investigationList) {
  await putRecordsInStore(
    IDB_STORES.INVESTIGATION.name,
    (investigationList || []).filter((item) => item?.title).map(withTitleIndexField),
  )
}

export async function getAllInvestigationInIdb() {
  return await getAllRecordsFromStore(IDB_STORES.INVESTIGATION.name)
}

export async function searchInvestigationInIdb(searchTerm) {
  if (!searchTerm) {
    return await getAllInvestigationInIdb()
  }
  return await searchItemsInIdb(searchTerm, IDB_STORES.INVESTIGATION.name, 'titleIDX', true)
}

export async function getInvestigationByIds(investigationByIds) {
  return await getItemsByIds(investigationByIds, IDB_STORES.INVESTIGATION.name)
}

export async function findAllPanelsWithGivenTestId(testId) {
  const allItems = await getAllRecordsFromStore(IDB_STORES.INVESTIGATION.name)
  return allItems.filter(
    (item) => item?.investigationType === 'panel' && item?.testIds?.includes(testId),
  )
}

// IDB_STORES.PSYCH_ASSESSMENTS.name:

export async function addOrUpdatePsychAssessmentsInIdb(psychAssessmentsList) {
  await putRecordsInStore(
    IDB_STORES.PSYCH_ASSESSMENTS.name,
    (psychAssessmentsList || []).filter((item) => item?.title).map(withTitleIndexField),
  )
}

export async function getAllPsychAssessmentsInIdb() {
  return await getAllRecordsFromStore(IDB_STORES.PSYCH_ASSESSMENTS.name)
}

export async function searchPsychAssessmentsInIdb(searchTerm) {
  if (!searchTerm) {
    return await getAllPsychAssessmentsInIdb()
  }
  return await searchItemsInIdb(searchTerm, IDB_STORES.PSYCH_ASSESSMENTS.name, 'titleIDX', true)
}

export async function getPsychAssessmentsByIds(psychAssessmentsByIds) {
  return await getItemsByIds(psychAssessmentsByIds, IDB_STORES.PSYCH_ASSESSMENTS.name)
}

// IDB_STORES.GENERIC_DRUGS.name:

export async function addOrUpdateGenericDrugsInIdb(genericDrugsList) {
  await putRecordsInStore(
    IDB_STORES.GENERIC_DRUGS.name,
    (genericDrugsList || []).filter((item) => item?.title).map(withTitleIndexField),
  )
}

export async function getAllGenericDrugsInIdb() {
  return await getAllRecordsFromStore(IDB_STORES.GENERIC_DRUGS.name)
}

export async function searchGenericDrugsInIdb(searchTerm) {
  if (!searchTerm) {
    // capped index read — returning the whole 60k+ store materialized every record
    const db = await getDB()
    return await db.getAllFromIndex(IDB_STORES.GENERIC_DRUGS.name, 'titleIDX', null, 100)
  }
  const results = await searchStoreByTitleFast(IDB_STORES.GENERIC_DRUGS.name, searchTerm, {
    limit: TITLE_RESULT_LIMIT,
  })
  const query = String(searchTerm).trim().toLowerCase()
  const prefixRank = (record) =>
    String(record.lowerCaseIndexField || '').startsWith(query) ? 0 : 1
  return results.sort((a, b) => {
    const byPrefix = prefixRank(a) - prefixRank(b)
    if (byPrefix !== 0) return byPrefix
    const titleA = a.lowerCaseIndexField || ''
    const titleB = b.lowerCaseIndexField || ''
    return titleA < titleB ? -1 : titleA > titleB ? 1 : 0
  })
}

export async function findGenericDrugInIdbByMedKey(searchGenericNameId) {
  if (searchGenericNameId == null || searchGenericNameId === '') {
    return null
  }
  const searchKey = Number(searchGenericNameId)
  if (Number.isNaN(searchKey)) {
    return null
  }
  // medKeyIDX works now that the store is plaintext; medKey may be stored as number
  // or string depending on the record's origin, so probe both key types
  try {
    const db = await getDB()
    const byNumber = await db.getFromIndex(IDB_STORES.GENERIC_DRUGS.name, 'medKeyIDX', searchKey)
    if (byNumber) return byNumber
    const byString = await db.getFromIndex(
      IDB_STORES.GENERIC_DRUGS.name,
      'medKeyIDX',
      String(searchGenericNameId),
    )
    if (byString) return byString
  } catch (error) {
    // index unavailable (mid-upgrade) — treat as not found
  }
  // no full-store fallback scan: post-v14 every record is indexed, and a 60k-record
  // materialization froze slow machines for a lookup that legitimately misses
  return null
}

export async function getGenericDrugsByIds(genericDrugsByIds) {
  return await getItemsByIds(genericDrugsByIds, IDB_STORES.GENERIC_DRUGS.name)
}

// IDB_STORES.PRESCRIPTION_INTEL.name: cached per-doctor suggestion sets (keyed reads only)

export async function putIntelRecordsInIdb(records) {
  await putRecordsInStore(IDB_STORES.PRESCRIPTION_INTEL.name, records)
}

export async function getIntelRecordFromIdb(id) {
  return await getRecordByIdFromStore(IDB_STORES.PRESCRIPTION_INTEL.name, id)
}

// IDB_STORES.DRUGS.name:

export async function addOrUpdateDrugsInIdb(drugsList) {
  await putRecordsInStore(
    IDB_STORES.DRUGS.name,
    (drugsList || [])
      .filter((drug) => drug?.name)
      .map((drug) => ({ ...drug, lowerCaseIndexField: drug.name.toLowerCase() })),
  )
}

export async function addOrUpdateGenericDrugsInStore(genericDrugNamesList) {
  await putRecordsInStore(
    IDB_STORES.GENERIC_DRUGS.name,
    (genericDrugNamesList || [])
      .filter((drug) => drug?.name)
      .map((drug) => ({ ...drug, lowerCaseIndexField: drug.name.toLowerCase() })),
  )
}

export async function getAllDrugsFromIdb() {
  return await getAllRecordsFromStore(IDB_STORES.DRUGS.name)
}

export async function searchDrugsInIdb(searchTerm, currentMentorId) {
  const query = String(searchTerm || '')
    .trim()
    .toLowerCase()
  if (!query) {
    // the empty dropdown state is served by prescription-intel suggestions — returning
    // every record here forced a full-store materialization on first click
    return []
  }
  const results = await searchStoreByTitleFast(IDB_STORES.DRUGS.name, query, {
    limit: TITLE_RESULT_LIMIT,
  })
  // cheap ranking over an already-capped set: own drugs, then public, prefix hits
  // first, formula-linked entries next, plain string compare (no localeCompare)
  const priority = (record) => {
    if (record.doctorId === currentMentorId) return 0
    if (!record.doctorId || record.doctorId === '' || record.availableToAll === true) return 1
    return 2
  }
  const prefixRank = (record) =>
    String(record.lowerCaseIndexField || '').startsWith(query) ? 0 : 1
  return results
    .sort((a, b) => {
      const byPriority = priority(a) - priority(b)
      if (byPriority !== 0) return byPriority
      const byPrefix = prefixRank(a) - prefixRank(b)
      if (byPrefix !== 0) return byPrefix
      const byFormula = (a.genericNameId ? 0 : 1) - (b.genericNameId ? 0 : 1)
      if (byFormula !== 0) return byFormula
      const titleA = a.lowerCaseIndexField || ''
      const titleB = b.lowerCaseIndexField || ''
      return titleA < titleB ? -1 : titleA > titleB ? 1 : 0
    })
    .slice(0, TITLE_RESULT_LIMIT)
}

export async function findDrugsByRelatedName(relatedName) {
  if (!relatedName || relatedName.trim() === '') {
    return []
  }
  // index read (v15 relatedNameIDX) — the old full-store filter materialized every
  // drug record on the main thread each time a drug was picked in the Rx table
  try {
    const db = await getDB()
    return await db.getAllFromIndex(IDB_STORES.DRUGS.name, 'relatedNameIDX', relatedName, 50)
  } catch (error) {
    // pre-v15 database (upgrade pending in another tab) — better empty than frozen
    return []
  }
}

export async function getDrugsByIDs(drugIds) {
  return await getItemsByIds(drugIds, IDB_STORES.DRUGS.name)
}

//PATIENTS:

export async function addOrUpdatePatientsInIdb(patientsList) {
  await putRecordsInStore(
    IDB_STORES.PATIENTS.name,
    (patientsList || [])
      .filter((patient) => patient?.name)
      .map((patient) => ({
        ...patient,
        ...(patient?.name.toLowerCase() && { nameIDX: patient?.name.toLowerCase() }),
        ...(patient?.mobile && { mobileIDX: patient?.mobile }),
        ...(patient?.email && { emailIDX: patient?.email }),
        ...(patient?.patientNumber && { patientNumberIDX: patient?.patientNumber }),
      })),
  )
}

export async function getAllPatientsFromIdb() {
  return await getAllRecordsFromStore(IDB_STORES.PATIENTS.name)
}

export async function searchPatientsInIdb(searchTerm) {
  if (!searchTerm) {
    const items = await getAllPatientsFromIdb()
    return (items || []).slice(0, 20)
  }
  const nameResults = await searchItemsInIdb(searchTerm, IDB_STORES.PATIENTS.name, 'nameIDX', false)
  const mobileResults = await searchItemsInIdb(
    searchTerm,
    IDB_STORES.PATIENTS.name,
    'mobileIDX',
    false,
  )
  const emailResults = await searchItemsInIdb(
    searchTerm,
    IDB_STORES.PATIENTS.name,
    'emailIDX',
    false,
  )
  const patientNumberResults = await searchItemsInIdb(
    searchTerm,
    IDB_STORES.PATIENTS.name,
    'patientNumberIDX',
    false,
  )

  const allResults = [...nameResults, ...mobileResults, ...emailResults, ...patientNumberResults]

  const uniqueResults = Array.from(new Set(allResults.map((patient) => patient.id))).map((id) =>
    allResults.find((patient) => patient.id === id),
  )

  return uniqueResults
}

export async function getPatientsByIds(patientIds) {
  return await getItemsByIds(patientIds, IDB_STORES.PATIENTS.name)
}

// CLINICAL ENTITIES (clinical-diagnosis / clinical-symptom / clinical-descriptor):
// ICD-10-aware stores. Search matches title (contains), altTitles (contains) and
// code/icd10Code (prefix, so typing "F32" works), mirroring the server's behaviour;
// specializationId / leafOnly filters replicate HealthEntityDao semantics (missing
// specializationIds = unclassified = always visible; leafOnly excludes only leaf:false).

const CLINICAL_ENTITY_STORE_BY_PARAM = {
  'clinical-diagnosis': IDB_STORES.CLINICAL_DIAGNOSIS.name,
  'clinical-symptom': IDB_STORES.CLINICAL_SYMPTOMS.name,
  'clinical-descriptor': IDB_STORES.CLINICAL_DESCRIPTORS.name,
}

export const CLINICAL_ENTITY_PARAMS_WITH_IDB_STORE = Object.keys(CLINICAL_ENTITY_STORE_BY_PARAM)

export async function addOrUpdateClinicalEntitiesInIdb(entityParam, items) {
  const storeName = CLINICAL_ENTITY_STORE_BY_PARAM[entityParam]
  if (!storeName) {
    return
  }
  await putRecordsInStore(
    storeName,
    (items || []).filter((item) => item?.title).map(withTitleIndexField),
  )
}

export async function clinicalEntityStoreHasData(entityParam) {
  const storeName = CLINICAL_ENTITY_STORE_BY_PARAM[entityParam]
  if (!storeName) {
    return false
  }
  const records = await getAllRecordsFromStore(storeName)
  return records.length > 0
}

export async function getClinicalEntitiesByIds(entityParam, ids) {
  const storeName = CLINICAL_ENTITY_STORE_BY_PARAM[entityParam]
  if (!storeName) {
    return []
  }
  return await getItemsByIds(ids, storeName)
}

function clinicalEntityMatchesSearch(record, searchLowercase) {
  if (!searchLowercase) {
    return true
  }
  if (record?.title?.toLowerCase()?.includes(searchLowercase)) {
    return true
  }
  if ((record?.altTitles || []).some((alt) => String(alt).toLowerCase().includes(searchLowercase))) {
    return true
  }
  const code = record?.icd10Code || record?.code
  return Boolean(code && String(code).toLowerCase().startsWith(searchLowercase))
}

export async function searchClinicalEntitiesInIdb(
  entityParam,
  searchTerm,
  { disciplineSlugs, leafOnly } = {},
) {
  const storeName = CLINICAL_ENTITY_STORE_BY_PARAM[entityParam]
  if (!storeName) {
    return []
  }
  const searchLowercase = (searchTerm || '').trim().toLowerCase()
  const allRecords = searchLowercase
    ? await getAllRecordsFromStore(storeName)
    : await getAllRecordsSortedByTitle(storeName)
  const disciplines = (disciplineSlugs || []).filter(Boolean)

  const results = allRecords.filter((record) => {
    if (record?.enabled === false || record?.deleted === true) {
      return false
    }
    if (leafOnly && record?.icdClassification?.leaf === false) {
      return false
    }
    if (disciplines.length > 0) {
      const tagged = record?.specializationIds
      if (Array.isArray(tagged) && tagged.length > 0 && !tagged.some((s) => disciplines.includes(s))) {
        return false
      }
    }
    return clinicalEntityMatchesSearch(record, searchLowercase)
  })

  // The cached source is already in canonical title order. Filtering preserves it.
  if (!searchLowercase) {
    return results
  }

  // code-prefix hits first (typing "F32" should surface F32 before "...F32..." titles),
  // then title-prefix, then the rest; alphabetical inside each band
  const rank = (record) => {
    const code = String(record?.icd10Code || record?.code || '').toLowerCase()
    if (searchLowercase && code.startsWith(searchLowercase)) return 0
    if (searchLowercase && record?.title?.toLowerCase()?.startsWith(searchLowercase)) return 1
    return 2
  }
  return results.sort((a, b) => {
    const rankDiff = rank(a) - rank(b)
    if (rankDiff !== 0) return rankDiff
    const titleDiff = compareHealthEntityTitles(a, b)
    if (titleDiff !== 0) return titleDiff
    const codeDiff = String(a?.icd10Code || a?.code || '').localeCompare(
      String(b?.icd10Code || b?.code || ''),
    )
    return codeDiff
  })
}

// DRUG ADVICE ENTITIES (dosage, duration, frequency, timing, type, note):

const DRUG_ADVICE_ENTITY_STORE_BY_PARAM = {
  [HE_DRUG_ADVICE_DOSAGE]: IDB_STORES.DRUG_ADVICE_DOSAGE.name,
  [HE_DRUG_ADVICE_DURATION]: IDB_STORES.DRUG_ADVICE_DURATION.name,
  [HE_DRUG_ADVICE_FREQUENCY]: IDB_STORES.DRUG_ADVICE_FREQUENCY.name,
  [HE_DRUG_ADVICE_TIMING]: IDB_STORES.DRUG_ADVICE_TIMING.name,
  [HE_DRUG_ADVICE_TYPE]: IDB_STORES.DRUG_ADVICE_TYPE.name,
  [HE_DRUG_ADVICE_NOTE]: IDB_STORES.DRUG_ADVICE_NOTE.name,
}

export const DRUG_ADVICE_ENTITY_PARAMS_WITH_IDB_STORE = Object.keys(
  DRUG_ADVICE_ENTITY_STORE_BY_PARAM,
)

export async function addOrUpdateDrugAdviceEntitiesInIdb(entityParam, items) {
  const storeName = DRUG_ADVICE_ENTITY_STORE_BY_PARAM[entityParam]
  if (!storeName) {
    return
  }
  await putRecordsInStore(
    storeName,
    (items || [])
      .filter((item) => item?.title || item?.shortTitle)
      .map((item) => ({
        ...item,
        lowerCaseIndexField: (item?.title || item?.shortTitle)?.toLowerCase(),
      })),
  )
}

export async function deleteDrugAdviceEntitiesFromIdb(entityParam, ids) {
  const storeName = DRUG_ADVICE_ENTITY_STORE_BY_PARAM[entityParam]
  if (!storeName) {
    return
  }
  await deleteRecordsFromStore(storeName, ids)
}

export async function getAllDrugAdviceEntitiesFromIdb(entityParam) {
  const storeName = DRUG_ADVICE_ENTITY_STORE_BY_PARAM[entityParam]
  if (!storeName) {
    return []
  }
  return await getAllRecordsFromStore(storeName)
}

export async function searchDrugAdviceEntitiesInIdb(entityParam, searchTerm) {
  const storeName = DRUG_ADVICE_ENTITY_STORE_BY_PARAM[entityParam]
  if (!storeName) {
    return []
  }
  if (!searchTerm) {
    return await getAllRecordsFromStore(storeName)
  }
  return await searchItemsInIdb(searchTerm, storeName, 'titleIDX', true)
}

//
//
//

export async function searchItemsInIdb(
  searchTerm,
  storeName,
  indexName,
  onlyStartsWithSearch = true,
  currentMentorId,
) {
  if (!searchTerm || searchTerm.trim() === '') {
    return []
  }
  const fieldName = INDEX_FIELD_BY_NAME[indexName] || indexName
  const searchLowercase = searchTerm.toLowerCase()

  const allRecords = await getAllRecordsFromStore(storeName)
  // always CONTAINS-match (the server list endpoints match contains too — prefix-only
  // hid valid entries); onlyStartsWithSearch now just controls prefix-first ranking
  let searchResults = allRecords.filter((record) => {
    const fieldValue = record?.[fieldName]
    if (!fieldValue) {
      return false
    }
    return String(fieldValue).toLowerCase().includes(searchLowercase)
  })

  if (storeName === IDB_STORES.DRUGS.name) {
    searchResults = searchResults.sort((a, b) => {
      // Helper function to get priority for sorting
      const getPriority = (item) => {
        // Priority 1: User's own content
        if (item.doctorId === currentMentorId) return 1
        // Priority 2: Content with no doctorId or availableToAll
        if (!item.doctorId || item.doctorId === '' || item.availableToAll === true) return 2
        // Priority 3: Other doctors' content
        return 3
      }

      const priorityA = getPriority(a)
      const priorityB = getPriority(b)

      // Primary Sort: By priority (lower number = higher priority)
      if (priorityA !== priorityB) {
        return priorityA - priorityB
      }

      // Secondary Sort: Entries with a genericNameId come first
      if (a.genericNameId && !b.genericNameId) return -1
      if (!a.genericNameId && b.genericNameId) return 1

      // Tertiary Sort: Alphabetically by title (A-Z)
      return (a.title || '').localeCompare(b.title || '')
    })
  } else if (onlyStartsWithSearch) {
    // prefix matches first (the old prefix-only behaviour, as a ranking), then alphabetical
    const startsWith = (record) =>
      String(record?.[fieldName] || '').toLowerCase().startsWith(searchLowercase) ? 0 : 1
    searchResults = searchResults.sort(
      (a, b) =>
        startsWith(a) - startsWith(b) ||
        String(a?.[fieldName] || '').localeCompare(String(b?.[fieldName] || '')),
    )
  }

  return searchResults
}

export async function searchItemsInIdbById(id, storeName) {
  return await getRecordByIdFromStore(storeName, id)
}

export async function getItemsByIds(ids, storeName) {
  if (!Array.isArray(ids) || ids.length === 0) {
    return []
  }
  const results = await Promise.all(ids.map((id) => getRecordByIdFromStore(storeName, id)))
  return results.filter((item) => item !== null)
}
